"use client";

import { useApiGet } from "@/hooks/use-api";
import { integrationKeys } from "./keys";
import type { McpIntegrationPublic, McpIntegrationsListResponse } from "./types";

/**
 * useMcpIntegrations
 *
 * Lists the current user's MCP integration tokens for a workspace
 * (GET /api/mcp-integrations?workspaceId=…&scope=own). Fetched only when the
 * settings "Closot MCP" tab opens.
 *
 * Usage:
 *   const { activeIntegrations, isConnected, isLoading } = useMcpIntegrations(workspaceId);
 */
export function useMcpIntegrations(workspaceId: string) {
  const { data, isLoading, isError, refetch } = useApiGet<McpIntegrationsListResponse>(
    integrationKeys.mcp(workspaceId),
    `/api/mcp-integrations?workspaceId=${encodeURIComponent(workspaceId)}&scope=own`,
    undefined,
    {
      enabled: !!workspaceId,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const integrations: McpIntegrationPublic[] = data?.integrations ?? [];
  const activeIntegrations = integrations.filter((i) => !i.revokedAt);

  return {
    integrations,
    activeIntegrations,
    isConnected: activeIntegrations.length > 0,
    isLoading,
    isError,
    refetch,
  };
}
